import { fetchFile } from '@ffmpeg/util';
import {
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
    preloadFFmpeg,
} from '../compress/compressVideoEngine';

export const MIN_MERGE_FILES = 2;
export const MAX_MERGE_FILE_BYTES = 200 * 1024 * 1024;

export function mergedVideoName() {
    return `merged-${Date.now()}.mp4`;
}

/**
 * Concat demuxer with stream copy (reference site). Re-encode only if copy fails.
 * @param {File[]} files
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function mergeVideos(files, onProgress = () => {}) {
    if (!Array.isArray(files) || files.length < MIN_MERGE_FILES) {
        throw new Error('NEED_MORE_FILES');
    }

    const ffmpeg = await loadFFmpeg();
    const inputNames = [];
    const listFileName = 'merge-inputs.txt';
    const outputName = 'merged-output.mp4';

    const safeDelete = async (name) => {
        try { await ffmpeg.deleteFile(name); } catch (_) { /* ignore */ }
    };

    try {
        const staged = files.map((file, i) => {
            const ext = file.name.includes('.')
                ? file.name.substring(file.name.lastIndexOf('.'))
                : '.mp4';
            const inputName = `merge-input-${i}${ext}`;
            inputNames.push(inputName);
            return { inputName, file };
        });

        await Promise.all(staged.map(async ({ inputName, file }) =>
            ffmpeg.writeFile(inputName, await fetchFile(file)),
        ));

        const concatFileContent = inputNames.map((name) => `file '${name}'`).join('\n');
        await ffmpeg.writeFile(listFileName, new TextEncoder().encode(concatFileContent));

        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const concatBase = [
            '-f', 'concat',
            '-safe', '0',
            '-i', listFileName,
        ];

        // Fast path: stream copy (reference site default).
        let exitCode = await ffmpeg.exec([
            ...concatBase,
            '-c', 'copy',
            '-fflags', '+genpts',
            '-movflags', '+faststart',
            outputName,
        ]);

        // Slow fallback only when codecs/params differ.
        if (exitCode !== 0) {
            await safeDelete(outputName);
            exitCode = await ffmpeg.exec([
                ...concatBase,
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-crf', '18',
                '-c:a', 'aac',
                '-movflags', '+faststart',
                outputName,
            ]);
        }

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            throw new Error('INCOMPATIBLE');
        }

        const data = await ffmpeg.readFile(outputName);
        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (
            error?.message === 'NEED_MORE_FILES'
            || error?.message === 'INCOMPATIBLE'
            || error?.message === 'EMPTY_OUTPUT'
        ) {
            throw error;
        }
        throw new Error('MERGE_FAILED');
    } finally {
        for (const name of inputNames) {
            await safeDelete(name);
        }
        await safeDelete(listFileName);
        await safeDelete(outputName);
    }
}
